import axios, { AxiosRequestConfig, AxiosError } from 'axios';
import { BehaviorSubject } from 'rxjs';
import { IdentityProvider } from './Auth/identityProvider';

export interface CallResult<T> {
  data: T | null;
  error: { message: string; errorResponse?: AxiosError } | null;
}

export type ApiUrl = string;
export type CallExternalApiOptions = Omit<AxiosRequestConfig, 'url'> & {
  url: ApiUrl;
};

// The API checks a token against Okta unless the request names another source, so only Entra is named.
export const authHeaders = (token: string, source?: IdentityProvider): Record<string, string> => ({
  Authorization: `Bearer ${token}`,
  ...(source === 'entra' ? { 'x-auth-source': 'entra' } : {})
});

class APIService {
  private token: string | null = null;
  private authSource: IdentityProvider = 'okta';
  private _hasToken$ = new BehaviorSubject<boolean>(false);

  setToken(token: string, source: IdentityProvider = 'okta') {
    this.token = token;
    this.authSource = source;
    this._hasToken$.next(true);
  }

  clearToken() {
    this.token = null;
    this._hasToken$.next(false);
  }

  get hasToken$(): BehaviorSubject<boolean> {
    return this._hasToken$;
  }

  async callExternalApi<T>(config: CallExternalApiOptions, skipAuth = false): Promise<CallResult<T>> {
    if (!this.token && !skipAuth) {
      return {
        data: null,
        error: { message: 'Missing token' }
      };
    }

    try {
      const response = await axios({
        ...config,
        baseURL: process.env.REACT_APP_API_SERVER_URL,
        headers: {
          ...config.headers,
          ...(this.token && !skipAuth ? authHeaders(this.token, this.authSource) : {})
        }
      });

      return { data: response?.data, error: null, headers: response?.headers } as CallResult<T>;
    } catch (error) {
      const message = await this.extractErrorMessage(error as AxiosError);
      return {
        data: null,
        error: {
          message,
          errorResponse: error as AxiosError
        },
        headers: null
      } as CallResult<T>;
    }
  }

  private async extractErrorMessage(error: AxiosError): Promise<string> {
    const data = error.response?.data;

    if (data instanceof Blob) {
      try {
        const text = await this.readBlobAsText(data);
        try {
          const parsed = JSON.parse(text) as { message?: unknown };
          if (typeof parsed.message === 'string' && parsed.message) return parsed.message;
          if (Array.isArray(parsed.message) && parsed.message.length) return parsed.message.join(', ');
          return this.truncateRawBody(text) || error.message;
        } catch {
          return this.truncateRawBody(text) || error.message;
        }
      } catch {
        return error.message;
      }
    }

    const message = (data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message) return message;
    if (Array.isArray(message) && message.length) return message.join(', ');
    if (typeof data === 'string' && data) return this.truncateRawBody(data);
    return error.message;
  }

  private truncateRawBody(text: string): string {
    const MAX_RAW_BODY_MESSAGE_LENGTH = 300;
    return text.length > MAX_RAW_BODY_MESSAGE_LENGTH ? `${text.slice(0, MAX_RAW_BODY_MESSAGE_LENGTH)}…` : text;
  }

  private readBlobAsText(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result ?? ''));
      reader.onerror = () => reject(reader.error);
      reader.readAsText(blob);
    });
  }
}

export const apiService = new APIService();
